// Lib
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

// Utils
import { getElementId, getImageProp } from '../../../../../../common/elements/utils/elementPropertyUtils';
import { parseUrl } from '../../../../../../common/utils/urlUtil';
import { readCurrentElementFromStore } from '../../utils/imageModalElementStoreUtils';
import { getGridSize } from '../../../../../utils/grid/gridSizeSelector';
import getAttachment from '../../../../attachments/getAttachment';
import { getFetching } from '../../../../attachments/attachmentsSelector';
import { prop } from '../../../../../../common/utils/immutableHelper';

// Actions
import { navigateToElement } from '../../../../../reducers/navigationActions';
import { setSelectedElements } from '../../../../selection/selectionActions';

// Utils
import { hasAltModifier, hasCommandModifier } from '../../../../../utils/keyboard/keyboardUtility';
import { getImageModalZoomKeyboardAction } from '../../utils/imageModalZoomKeyboardUtils';
import { isRouteParamEnabled } from '../../imageModalUtils';

// Hooks
import useDocumentKeyEventHandler from '../../../../drawing/drawingEditor/drawingEditorCanvas/useDocumentKeyEventHandler';
import useStalledBoolean from '../../../../../utils/react/useStalledBoolean';
import useImageModalZoom from '../../hooks/useImageModalZoom';
import { ImageModalV2Mode, useImageModalV2Mode } from './useImageModalV2Mode';
import { useFinishEditingAfterViewLoad } from './useFinishEditingAfterViewLoad';
import { useImageModalV2Layout } from './useImageModalV2Layout';
import { useImageModalNavigation } from './useImageModalNavigation';
import { useImageModalV2BackgroundClose } from './useImageModalV2BackgroundClose';
import { useImageModalCaptionHideOnClose } from './useImageModalCaptionHideOnClose';

// Constants
import { KEY_CODES } from '../../../../../utils/keyboard/keyConstants';
import { ELEMENT_ATTACHMENT_ACTION_TYPES } from '../../../../../../common/elements/elementConstants';

// Types
import { ImageModalV2EditModeProps, ImageModalV2ShellProps } from '../imageModalV2Types';
import { ReduxState } from '../../../../../types/reduxTypes';

const useImageModalV2TransferMode = ({
    hasAttachmentUpload,
    hasAttachmentDownload,
}: {
    hasAttachmentUpload: boolean;
    hasAttachmentDownload: boolean;
}) => {
    const lastTransferModeRef = useRef<ImageModalV2Mode.UPLOAD | ImageModalV2Mode.DOWNLOAD | null>(null);
    const hasActiveAttachmentTransfer = hasAttachmentUpload || hasAttachmentDownload;
    const showTransferMode = useStalledBoolean(hasActiveAttachmentTransfer, 0, 1000);

    if (hasAttachmentUpload) {
        lastTransferModeRef.current = ImageModalV2Mode.UPLOAD;
    } else if (hasAttachmentDownload) {
        lastTransferModeRef.current = ImageModalV2Mode.DOWNLOAD;
    }

    return showTransferMode ? lastTransferModeRef.current : null;
};

/**
 * Owns V2 image modal orchestration: route mode, layout measurement, navigation, and edit contracts.
 */
export const useImageModalShell = ({
    element,
    isEditable,
    location,
    close,
    closeCallbackRef,
}: ImageModalV2ShellProps) => {
    const dispatch = useDispatch();
    const attachment = useSelector((state: ReduxState) => getAttachment(state, { element }));
    const gridSize = useSelector(getGridSize);
    const attachmentActionType = prop('actionType', attachment);
    const isAttachmentTransferActive = !!getFetching(attachment);
    const hasAttachmentUpload =
        isAttachmentTransferActive && attachmentActionType === ELEMENT_ATTACHMENT_ACTION_TYPES.UPLOAD;
    const hasAttachmentDownload =
        isAttachmentTransferActive && attachmentActionType === ELEMENT_ATTACHMENT_ACTION_TYPES.DOWNLOAD;
    const transferMode = useImageModalV2TransferMode({ hasAttachmentUpload, hasAttachmentDownload });

    const modalRef = useRef<HTMLDivElement>(null);
    const primaryToolbarRef = useRef<HTMLDivElement>(null);
    const captionRef = useRef<HTMLDivElement>(null);
    const pendingCroppedImageCreationRef = useRef<(() => void) | null>(null);

    const layout = useImageModalV2Layout({ element, attachment, gridSize, modalRef, captionRef });
    const imageZoom = useImageModalZoom();
    const { isZoomed, resetZoom, setContentSize, zoomIn, zoomOut } = imageZoom;

    const elementId = getElementId(element);
    const isEditing = isRouteParamEnabled(location?.query?.editing);
    const isEditingBackground = isRouteParamEnabled(location?.query?.['editing-background']);
    const isEditingDrawing = isRouteParamEnabled(location?.query?.['editing-drawing']);
    const returnToCanvasRef = useRef(isEditing || isEditingBackground || isEditingDrawing);

    const setRouteModeForElement = useCallback(
        (nextMode: ImageModalV2Mode) => {
            dispatch(
                navigateToElement({
                    elementId,
                    params: {
                        editing: nextMode === ImageModalV2Mode.CROP,
                        'editing-background': nextMode === ImageModalV2Mode.BACKGROUND,
                        'editing-drawing': nextMode === ImageModalV2Mode.DRAW,
                    },
                }),
            );
        },
        [dispatch, elementId],
    );

    const { mode, setMode, isToolPanelOpen } = useImageModalV2Mode({
        isEditable,
        isEditing,
        isEditingBackground,
        isEditingDrawing,
        isUploadingAttachment: transferMode === ImageModalV2Mode.UPLOAD,
        isDownloadingAttachment: transferMode === ImageModalV2Mode.DOWNLOAD,
        setRouteMode: setRouteModeForElement,
    });

    const { canNavigate, orderedImageElements, navigateToPreviousImage, navigateToNextImage } = useImageModalNavigation(
        {
            element,
            enabled: mode === ImageModalV2Mode.VIEW,
        },
    );

    const finishEditingMode = useCallback(() => {
        if (returnToCanvasRef.current) {
            close();
            return;
        }

        setMode(ImageModalV2Mode.VIEW);
    }, [close, setMode]);

    // Hold the frozen editor frame after a save until the view image has loaded the new source.
    const {
        beginSaveHandoff,
        cancelSaveHandoff,
        finishEditingAfterSave: finishEditingAfterSaveWithSourcePath,
        handleViewImageLoaded,
    } = useFinishEditingAfterViewLoad(finishEditingMode);

    /**
     * Resolves the pathname of the just-persisted image content from the store (the element prop can be a
     * render behind at save time), so the hand-off can tell the saved render's loads apart from other-size
     * loads of the pre-save image.
     */
    const finishEditingAfterSave = useCallback(() => {
        const currentElement = readCurrentElementFromStore(dispatch, getElementId(element), element);
        const persistedImageUrl: string | undefined = prop('original', getImageProp(currentElement));
        const persistedImagePath = persistedImageUrl
            ? (parseUrl(persistedImageUrl, window.location.origin)?.pathname ?? null)
            : null;
        finishEditingAfterSaveWithSourcePath(persistedImagePath);
    }, [dispatch, element, finishEditingAfterSaveWithSourcePath]);

    const { handleModalMouseDown, handleModalClick } = useImageModalV2BackgroundClose({ close });

    const setCaptionHiddenOnModalClose = useImageModalCaptionHideOnClose(elementId);

    /** Queues synchronous canvas work until the saving toast has completed its protected entrance. */
    const scheduleCroppedImageCreation = useCallback((createCroppedImage: () => void) => {
        pendingCroppedImageCreationRef.current = createCroppedImage;
    }, []);

    /** Starts the queued canvas work exactly once after the saving-toast handoff has completed. */
    const runPendingCroppedImageCreation = useCallback(() => {
        const createCroppedImage = pendingCroppedImageCreationRef.current;
        pendingCroppedImageCreationRef.current = null;
        createCroppedImage?.();
    }, []);

    useEffect(
        () => () => {
            pendingCroppedImageCreationRef.current = null;
        },
        [],
    );

    const onKeyDown = useCallback(
        (event: KeyboardEvent) => {
            const isZoomInteractionEnabled =
                mode === ImageModalV2Mode.VIEW ||
                mode === ImageModalV2Mode.UPLOAD ||
                mode === ImageModalV2Mode.DOWNLOAD;

            if (event.keyCode === KEY_CODES.ESC) {
                // Zooming is the most recent thing the user did, so Esc undoes that first rather than
                // closing the modal out from under them. Gated on the mode rather than `isZoomed` alone:
                // the reset on mode change lands in an effect, so zoom can still read as active for a
                // moment after entering crop/background/draw.
                if (isZoomInteractionEnabled && isZoomed) {
                    event.preventDefault();
                    event.stopPropagation();
                    resetZoom();
                    return;
                }

                // In crop/background mode the first Esc dismisses the editing tool (like Cancel — which
                // closes the modal instead when it was opened straight into the tool from the canvas).
                // Draw mode intercepts Esc itself the same way, and in view mode the modal binding closes
                // the modal as normal.
                if (mode !== ImageModalV2Mode.CROP && mode !== ImageModalV2Mode.BACKGROUND) return;

                event.preventDefault();
                event.stopPropagation();
                finishEditingMode();
                return;
            }

            if (hasCommandModifier(event)) {
                const zoomAction = getImageModalZoomKeyboardAction(event.keyCode);

                if (!isZoomInteractionEnabled || !zoomAction) return;

                event.preventDefault();
                event.stopPropagation();

                switch (zoomAction) {
                    case 'zoomIn':
                        zoomIn();
                        break;
                    case 'zoomOut':
                        zoomOut();
                        break;
                    case 'reset':
                        resetZoom();
                        break;
                    default:
                        break;
                }

                return;
            }

            if (hasAltModifier(event) || !isEditable) return;

            switch (event.keyCode) {
                case KEY_CODES.P:
                case KEY_CODES.D:
                    setMode(ImageModalV2Mode.DRAW);
                    break;
                default:
                    break;
            }
        },
        [finishEditingMode, isEditable, isZoomed, mode, resetZoom, setMode, zoomIn, zoomOut],
    );

    useDocumentKeyEventHandler({ onKeyDown, onKeyUp: undefined, onKeyPress: undefined });

    useEffect(() => {
        dispatch(setSelectedElements({ ids: [elementId], rangeAnchors: null, transactionId: undefined }));
    }, [dispatch, elementId]);

    useEffect(() => {
        (document.activeElement as HTMLElement)?.blur?.();
    }, [elementId, mode]);

    useEffect(() => {
        resetZoom();
    }, [elementId, mode, resetZoom]);

    useEffect(() => {
        // The zoom transform is applied to the frame (image + caption), so the zoom maths must use the
        // frame size (`contentStyle`) for anchoring/clamping, otherwise the caption is excluded.
        setContentSize({
            width: Number(layout.contentStyle.width) || 0,
            height: Number(layout.contentStyle.height) || 0,
        });
    }, [layout.contentStyle.height, layout.contentStyle.width, setContentSize]);

    const editModeProps = useMemo<ImageModalV2EditModeProps>(
        () => ({
            element,
            gridSize,
            attachment,
            finishEditingMode,
            beginSaveHandoff,
            cancelSaveHandoff,
            finishEditingAfterSave,
            scheduleCroppedImageCreation,
            setCaptionHiddenOnModalClose,
            closeCallbackRef,
        }),
        [
            attachment,
            beginSaveHandoff,
            cancelSaveHandoff,
            closeCallbackRef,
            element,
            finishEditingAfterSave,
            finishEditingMode,
            gridSize,
            setCaptionHiddenOnModalClose,
            scheduleCroppedImageCreation,
        ],
    );

    return {
        modalRef,
        primaryToolbarRef,
        captionRef,
        mode,
        setMode,
        isToolPanelOpen,
        layout,
        imageZoom,
        editModeProps,
        canNavigate,
        orderedImageElements,
        navigateToPreviousImage,
        navigateToNextImage,
        handleModalMouseDown,
        handleModalClick,
        handleViewImageLoaded,
        runPendingCroppedImageCreation,
    };
};
